import API_BASE_URL, {
  APP_ENV,
  FEATURE_SERVER_TAG_SUGGESTIONS,
  REACT_APP_SERVER_TAG_SUGGESTIONS_RAW,
  REACT_APP_SERVER_TAG_SUGGESTIONS_RUNTIME_RAW
} from '../config';
import { getAuthHeaders } from '../utils/authHeaders';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { listLexicon, replaceLexicon } from '../db/lexiconCache';
import type { HazardLexicon } from '../db/types';
import logger from '../utils/logger';

const SERVER_TAG_PREFIX = '[server-tag-suggestions]';

/** Metadata returned by the backend classifyHazardTags endpoint. */
export interface ClassifyTagsMeta {
  mode?: string;
  reason?: string;
  /** End-to-end classification duration in milliseconds (added by the handler). */
  duration_ms?: number;
  [key: string]: unknown;
}

/** Result of classifyTagsFromServer: success carries tags, failure carries a human-readable reason. */
export type ClassifyTagsResult =
  | { ok: true; tags: string[]; meta?: ClassifyTagsMeta }
  | { ok: false; reason: string };

/** Console diagnostics: dev / feature on, or set localStorage DEBUG_SERVER_TAG_SUGGESTIONS=1 */
function shouldLogServerTagSuggestions(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const v = window.localStorage.getItem('DEBUG_SERVER_TAG_SUGGESTIONS');
    if (v === '1' || v === 'true') return true;
  } catch {
    /* private mode / blocked storage */
  }
  return (
    FEATURE_SERVER_TAG_SUGGESTIONS ||
    APP_ENV === 'dev' ||
    process.env.NODE_ENV === 'development'
  );
}

function serverTagDebug(message: string, data?: Record<string, unknown>): void {
  if (!shouldLogServerTagSuggestions()) return;
  if (data !== undefined) {
    console.log(SERVER_TAG_PREFIX, message, data);
  } else {
    console.log(SERVER_TAG_PREFIX, message);
  }
}

class LexiconService {
  /**
   * Fetch the lexicon from the server and store it locally in Dexie.
   */
  async syncLexicon(): Promise<void> {
    if (!navigator.onLine) {
      console.log('Offline: Skipping lexicon sync.');
      return;
    }

    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/lexicon`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch lexicon: ${response.statusText}`);
      }

      const lexiconData: HazardLexicon[] = await response.json();

      if (lexiconData && lexiconData.length > 0) {
        // Clear old lexicon and replace with new
        await replaceLexicon(lexiconData);
        console.log(`Successfully synced ${lexiconData.length} lexicon entries.`);
      }
    } catch (error) {
      console.error('Error syncing lexicon:', error);
    }
  }

  /**
   * Retrieves the locally cached lexicon from Dexie.
   */
  async getLocalLexicon(): Promise<HazardLexicon[]> {
    try {
      const items = await listLexicon();
      console.log('Got local lexicon from Dexie:', items.length, 'items');
      return items;
    } catch (error) {
      console.error('Error getting local lexicon:', error);
      return [];
    }
  }

  /**
   * Generate tags for a given description based on the local lexicon.
   */
  generateTags(description: string, lexicon: HazardLexicon[]): string[] {
    if (!description || !lexicon || lexicon.length === 0) {
      return [];
    }

    console.log('Generating tags for description:', description, 'against', lexicon.length, 'lexicon entries');
    const tags = new Set<string>();
    const normalizedDesc = description.trim();

    for (const entry of lexicon) {
      if (!entry.term) continue;

      const term = entry.term.trim();
      if (!term) continue;

      let matched = false;

      if (entry.regex_pattern) {
        try {
          // Check if regex_pattern is a JSON array of strings
          if (entry.regex_pattern.startsWith('[') && entry.regex_pattern.endsWith(']')) {
            const patterns: string[] = JSON.parse(entry.regex_pattern);
            for (const pat of patterns) {
              const regex = compileLexiconRegex(pat);
              if (regex.test(normalizedDesc)) {
                matched = true;
                break;
              }
            }
          } else {
            // It's a single raw regex
            const regex = compileLexiconRegex(entry.regex_pattern);
            if (regex.test(normalizedDesc)) {
              matched = true;
            }
          }
        } catch (e) {
          console.warn(`Invalid regex for term "${term}":`, e);
          // Fallback to boundary word match if regex fails
          const boundaryRegex = new RegExp(`\\b${escapeRegExp(term)}\\b`, 'i');
          if (boundaryRegex.test(normalizedDesc)) {
            matched = true;
          }
        }
      } else {
        // Simple word boundary match using the term if no pattern is available or it fails
        // Match the term inside the text ignoring case. Use regex with word boundaries.
        const boundaryRegex = new RegExp(`\\b${escapeRegExp(term)}\\b`, 'i');
        if (boundaryRegex.test(normalizedDesc)) {
          matched = true;
        }
      }

      if (matched) {
        tags.add(term);
      }
    }

    console.log('Found tags:', tags);
    return Array.from(tags).sort();
  }

  /**
   * Ask the backend for lexicon-constrained tags (RAG + model path when enabled server-side).
   * Returns a discriminated result: `{ ok: true, tags, meta? }` on success, or
   * `{ ok: false, reason }` when skipped/failed (feature disabled, offline, HTTP error, etc.).
   */
  async classifyTagsFromServer(
    description: string,
    options?: { signal?: AbortSignal }
  ): Promise<ClassifyTagsResult> {
    const apiBaseDisplay = API_BASE_URL || '(empty — requests go to current origin / wrong host)';
    const baseContext = {
      FEATURE_SERVER_TAG_SUGGESTIONS,
      REACT_APP_SERVER_TAG_SUGGESTIONS_build:
        REACT_APP_SERVER_TAG_SUGGESTIONS_RAW || '(unset)',
      REACT_APP_SERVER_TAG_SUGGESTIONS_runtime:
        REACT_APP_SERVER_TAG_SUGGESTIONS_RUNTIME_RAW || '(unset)',
      API_BASE_URL: apiBaseDisplay,
      navigatorOnLine: typeof navigator !== 'undefined' ? navigator.onLine : '(no navigator)'
    };

    if (!FEATURE_SERVER_TAG_SUGGESTIONS) {
      const reason = 'AI tagging disabled (feature flag off)';
      serverTagDebug('classifyTagsFromServer skipped: feature disabled', {
        ...baseContext,
        reason:
          'FEATURE_SERVER_TAG_SUGGESTIONS is false — set window.__ENV__.REACT_APP_SERVER_TAG_SUGGESTIONS to "1" or "true" in env.js (runtime), or REACT_APP_SERVER_TAG_SUGGESTIONS at build time (restart dev server after .env changes).'
      });
      return { ok: false, reason };
    }

    if (!navigator.onLine) {
      const reason = 'Offline — cannot reach server';
      serverTagDebug('classifyTagsFromServer skipped: offline', {
        ...baseContext,
        reason: 'navigator.onLine is false'
      });
      return { ok: false, reason };
    }

    const trimmed = (description || '').trim();
    if (!trimmed) {
      serverTagDebug('classifyTagsFromServer skipped: empty description', { ...baseContext });
      return { ok: false, reason: 'Empty description' };
    }

    const classifyUrl = `${API_BASE_URL}/api/classifyHazardTags`;
    serverTagDebug('classifyTagsFromServer calling backend', {
      ...baseContext,
      url: classifyUrl,
      descriptionLength: trimmed.length
    });

    try {
      const response = await fetchWithAuth(classifyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ description: trimmed }),
        signal: options?.signal
      });
      if (!response.ok) {
        serverTagDebug('classifyHazardTags non-OK response', {
          ...baseContext,
          url: classifyUrl,
          status: response.status,
          statusText: response.statusText
        });
        logger.apiWarn('classifyHazardTags HTTP error', {
          status: response.status,
          statusText: response.statusText
        });
        return { ok: false, reason: `Server returned HTTP ${response.status} ${response.statusText}`.trim() };
      }
      const data = (await response.json()) as { tags?: string[]; meta?: ClassifyTagsMeta };
      const tags = Array.isArray(data.tags) ? data.tags.map((t) => String(t).trim()).filter(Boolean) : [];
      serverTagDebug('classifyHazardTags success', {
        ...baseContext,
        url: classifyUrl,
        tagCount: tags.length
      });
      return { ok: true, tags, meta: data.meta };
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      if (e.name === 'AbortError') {
        serverTagDebug('classifyTagsFromServer aborted', { ...baseContext, url: classifyUrl });
        return { ok: false, reason: 'Cancelled' };
      }
      serverTagDebug('classifyHazardTags request failed', {
        ...baseContext,
        url: classifyUrl,
        errorName: e.name,
        errorMessage: e.message
      });
      logger.apiWarn('classifyHazardTags failed', e);
      return { ok: false, reason: `Network error: ${e.message}` };
    }
  }
}

function compileLexiconRegex(pattern: string): RegExp {
  // Lexicon patterns are authored for Python's regex engine and may include inline flags
  // like (?i) or scoped variants like (?i:...), which are invalid in JavaScript.
  let normalizedPattern = pattern;
  let flags = 'i';

  const leadingInlineFlags = normalizedPattern.match(/^\(\?([imsx-]+)\)/);
  if (leadingInlineFlags) {
    normalizedPattern = normalizedPattern.slice(leadingInlineFlags[0].length);
    const foundFlags = leadingInlineFlags[1].replace('-', '');
    if (foundFlags.includes('m') && !flags.includes('m')) flags += 'm';
    if (foundFlags.includes('s') && !flags.includes('s')) flags += 's';
  }

  // Convert Python scoped inline flags (?i:...) to non-capturing groups (?:...).
  normalizedPattern = normalizedPattern.replace(/\(\?[imsx-]+:/g, '(?:');

  return new RegExp(normalizedPattern, flags);
}

// Helper to escape literal string into a regex pattern
function escapeRegExp(string: string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}

export const lexiconService = new LexiconService();
export default lexiconService;
